import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IdObject, Nil } from '@pbt/pbt-ui-components'

import MultiAttachmentDialog, {
  MultiAttachmentDialogProps,
} from '~/components/common/inputs/attachment/attachment-dialog/multi-attachment/MultiAttachmentDialog'
import MultiAttachmentFilters from '~/components/common/inputs/attachment/attachment-dialog/multi-attachment/MultiAttachmentFilters'
import {
  saveAttachmentHistoryDocuments,
  saveMultiAttachmentHistory,
  savePastMedicalRecord,
} from '~/store/actions/medicalHistory'
import { getMedicalHistoryIsSending } from '~/store/reducers/medicalHistory'
import { getAppointment } from '~/store/reducers/soap'
import { AttachmentGroup, FilesWithDetails } from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

import AttachmentHistoryDetails from './AttachmentHistoryDetails'

export interface MultiAttachmentHistoryDialogProps
  extends Omit<MultiAttachmentDialogProps, 'renderMultiAttachment'> {
  clientId: string | Nil
  fileLimit?: number
  patientId: string | Nil
  previousMedicalRecord: boolean
  title?: string
}

const MultiAttachmentHistoryDialog = ({
  onClose,
  open,
  clientId,
  patientId,
  fileLimit,
  title,
  previousMedicalRecord = false,
}: MultiAttachmentHistoryDialogProps) => {
  const dispatch = useDispatch()
  const isSending = useSelector(getMedicalHistoryIsSending)
  const { appointmentId, startDate } = useSelector(getAppointment) || {}

  const setCloseAfterSavedOn = useCloseAfterCreation(
    onClose,
    getMedicalHistoryIsSending,
  )

  const onDocumnentsUpload = (orderedItems: IdObject[]) => {
    setCloseAfterSavedOn()
    if (patientId) {
      dispatch(
        saveAttachmentHistoryDocuments(orderedItems, patientId, clientId),
      )
    }
  }

  const onFilesUpload = (group: AttachmentGroup) => {
    setCloseAfterSavedOn()
    if (patientId) {
      if (previousMedicalRecord) {
        dispatch(
          savePastMedicalRecord(
            { date: new Date(), ...group } as FilesWithDetails,
            patientId,
            clientId,
          ),
        )
      } else {
        dispatch(
          saveMultiAttachmentHistory(
            group as FilesWithDetails,
            patientId,
            clientId,
          ),
        )
      }
    }
  }

  const showDetails = !previousMedicalRecord

  return (
    <MultiAttachmentDialog
      open={open}
      renderMultiAttachment={({
        closeEdit,
        fileIndexForImageEdit,
        setFileIndexForImageEdit,
      }) => (
        <MultiAttachmentFilters
          ListWithFiltersProps={{
            ListItemProps: {
              showListItemActions: true,
            },
            DetailsComponent: AttachmentHistoryDetails,
            isLoading: isSending,
          }}
          closeEdit={closeEdit}
          date={startDate}
          eventId={appointmentId}
          fileIndexForImageEdit={fileIndexForImageEdit}
          fileLimit={fileLimit}
          hideFileSelect={previousMedicalRecord}
          patientId={patientId}
          setFileIndexForImageEdit={setFileIndexForImageEdit}
          showDetails={showDetails}
          onDocumnentsUpload={onDocumnentsUpload}
          onFilesUpload={onFilesUpload}
        />
      )}
      title={title}
      onClose={onClose}
    />
  )
}

export default MultiAttachmentHistoryDialog
